import { useEffect, useState, useCallback } from 'react';
import api from '../../api/axios';

const STATUS_BADGE = {
  unread:  { cls: 'badge-soft-warning',  label: 'Unread' },
  read:    { cls: 'badge-soft-secondary', label: 'Read' },
  replied: { cls: 'badge-soft-success',  label: 'Replied' },
};

const fmtDate = d => d ? new Date(d).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

function EmailDiagnostic() {
  const [config, setConfig]       = useState(null);
  const [testTo, setTestTo]       = useState('');
  const [testing, setTesting]     = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [open, setOpen]           = useState(false);

  const loadConfig = useCallback(() => {
    api.get('/auth/email-config').then(r => setConfig(r.data)).catch(() => {});
  }, []);

  useEffect(() => { if (open) loadConfig(); }, [open, loadConfig]);

  const sendTest = async () => {
    if (!testTo.trim()) return;
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post('/auth/test-email', { to: testTo.trim() });
      setTestResult({ ok: true, msg: r.data.message });
    } catch (err) {
      setTestResult({ ok: false, msg: err.response?.data?.error || err.message, code: err.response?.data?.code });
    } finally { setTesting(false); }
  };

  return (
    <div className="bg-white rounded shadow-sm p-3 mb-3">
      <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={() => setOpen(o => !o)}>
        <i className="fas fa-envelope-circle-check me-1"></i>Email Diagnostics
      </button>
      {open && (
        <div className="mt-3">
          {config ? (
            <table className="table table-sm mb-3" style={{ fontSize: '0.82rem' }}>
              <tbody>
                {Object.entries(config).map(([k, v]) => (
                  <tr key={k}>
                    <td className="text-muted" style={{ width: '50%' }}>{k}</td>
                    <td className={v.startsWith('✓') ? 'text-success fw-semibold' : 'text-danger fw-semibold'}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="text-muted small mb-2">Loading config…</div>}
          <div className="d-flex gap-2 align-items-center">
            <input
              className="form-control form-control-sm"
              placeholder="Send test email to…"
              value={testTo}
              onChange={e => { setTestTo(e.target.value); setTestResult(null); }}
              style={{ maxWidth: 240 }}
            />
            <button className="btn btn-sm btn-primary rounded-pill" onClick={sendTest} disabled={testing || !testTo.trim()}>
              {testing ? <span className="spinner-border spinner-border-sm"></span> : 'Send Test'}
            </button>
          </div>
          {testResult && (
            <div className={`alert alert-${testResult.ok ? 'success' : 'danger'} py-2 mt-2 small`}>
              {testResult.ok ? <i className="fas fa-check-circle me-1"></i> : <i className="fas fa-exclamation-circle me-1"></i>}
              {testResult.msg}{testResult.code && <span className="text-muted ms-1">({testResult.code})</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MessagesManager() {
  const [messages, setMessages]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying]   = useState(false);
  const [replyOk, setReplyOk]     = useState(false);
  const [replyErr, setReplyErr]   = useState('');
  const [filter, setFilter]       = useState('all');
  const [deleting, setDeleting]   = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/feedback').then(r => setMessages(r.data || [])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openMessage = async (m) => {
    setSelected(m);
    setReplyText('');
    setReplyOk(false);
    setReplyErr('');
    if (m.status === 'unread') {
      await api.put(`/feedback/${m.id}/read`).catch(() => {});
      setMessages(prev => prev.map(x => x.id === m.id ? { ...x, status: 'read' } : x));
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setReplying(true); setReplyErr(''); setReplyOk(false);
    try {
      await api.post(`/feedback/${selected.id}/reply`, { replyText });
      setReplyOk(true);
      setMessages(prev => prev.map(x => x.id === selected.id ? { ...x, status: 'replied' } : x));
      setSelected(s => ({ ...s, status: 'replied' }));
    } catch (err) {
      setReplyErr(err.response?.data?.error || 'Failed to send reply');
    } finally { setReplying(false); }
  };

  const deleteMsg = async (m) => {
    setDeleting(m.id);
    try {
      await api.delete(`/feedback/${m.id}`);
      setMessages(prev => prev.filter(x => x.id !== m.id));
      if (selected?.id === m.id) setSelected(null);
    } finally { setDeleting(null); }
  };

  const counts = {
    all: messages.length,
    unread: messages.filter(m => m.status === 'unread').length,
    replied: messages.filter(m => m.status === 'replied').length,
  };

  const filtered = messages.filter(m => filter === 'all' || m.status === filter);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-0">
            Messages
            {counts.unread > 0 && <span className="badge bg-danger ms-2" style={{ fontSize: '0.7rem' }}>{counts.unread} new</span>}
          </h4>
          <p className="text-muted small mb-0">Contact form submissions from the website</p>
        </div>
        <button className="btn btn-sm btn-outline-secondary rounded-pill" onClick={load}>
          <i className="fas fa-sync-alt me-1"></i>Refresh
        </button>
      </div>

      <EmailDiagnostic />

      {/* Filter tabs */}
      <div className="bg-white rounded shadow-sm p-2 mb-3 d-flex gap-2">
        {[['all', 'All'], ['unread', 'Unread'], ['read', 'Read'], ['replied', 'Replied']].map(([val, lbl]) => (
          <button key={val} className={`btn btn-sm rounded-pill px-3 ${filter === val ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setFilter(val)}>
            {lbl} {val !== 'replied' && <span className="ms-1">({val === 'all' ? counts.all : val === 'unread' ? counts.unread : messages.filter(m => m.status === val).length})</span>}
          </button>
        ))}
      </div>

      <div className="row g-3" style={{ height: 'calc(100vh - 260px)', minHeight: 400 }}>
        {/* Message list */}
        <div className="col-md-5">
          <div className="bg-white rounded shadow-sm overflow-auto h-100">
            {loading ? (
              <div className="text-center py-5 text-muted"><span className="spinner-border spinner-border-sm me-2"></span>Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-inbox fa-2x mb-2 d-block opacity-25"></i>No messages
              </div>
            ) : filtered.map(m => (
              <div
                key={m.id}
                className="p-3 border-bottom"
                style={{ cursor: 'pointer', background: selected?.id === m.id ? '#f0f4ff' : m.status === 'unread' ? '#fffbf0' : '#fff', transition: 'background 0.1s' }}
                onClick={() => openMessage(m)}
              >
                <div className="d-flex justify-content-between align-items-start">
                  <div className="fw-semibold small" style={{ fontWeight: m.status === 'unread' ? 700 : 500 }}>
                    {m.status === 'unread' && <span className="text-danger me-1">●</span>}
                    {m.name}
                  </div>
                  <span className={`badge ${STATUS_BADGE[m.status]?.cls || 'bg-secondary'}`} style={{ fontSize: '0.65rem' }}>
                    {STATUS_BADGE[m.status]?.label || m.status}
                  </span>
                </div>
                <div className="text-muted" style={{ fontSize: '0.78rem' }}>{m.email}</div>
                <div className="text-truncate mt-1" style={{ fontSize: '0.82rem' }}>{m.subject || <em className="text-muted">(no subject)</em>}</div>
                <div className="text-muted" style={{ fontSize: '0.7rem', marginTop: 2 }}>{fmtDate(m.created_at)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Message detail + reply */}
        <div className="col-md-7">
          <div className="bg-white rounded shadow-sm h-100 d-flex flex-column">
            {!selected ? (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted flex-column gap-2">
                <i className="fas fa-envelope-open fa-2x opacity-25"></i>
                <span className="small">Select a message to read</span>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="p-4 border-bottom">
                  <div className="d-flex justify-content-between align-items-start mb-1">
                    <h5 className="fw-bold mb-0">{selected.subject || <em className="text-muted fw-normal">(No subject)</em>}</h5>
                    <button className="btn btn-sm btn-outline-danger rounded-pill px-3" disabled={deleting === selected.id} onClick={() => deleteMsg(selected)}>
                      {deleting === selected.id ? <span className="spinner-border spinner-border-sm"></span> : <><i className="fas fa-trash me-1"></i>Delete</>}
                    </button>
                  </div>
                  <div className="d-flex align-items-center gap-3 text-muted small">
                    <span><i className="fas fa-user me-1"></i><strong>{selected.name}</strong></span>
                    <a href={`mailto:${selected.email}`} className="text-decoration-none text-primary"><i className="fas fa-envelope me-1"></i>{selected.email}</a>
                    <span><i className="fas fa-clock me-1"></i>{fmtDate(selected.created_at)}</span>
                  </div>
                </div>

                {/* Body */}
                <div className="p-4 flex-grow-1 overflow-auto" style={{ whiteSpace: 'pre-wrap', fontSize: '0.92rem', lineHeight: 1.7 }}>
                  {selected.message}
                </div>

                {/* Reply box */}
                <div className="p-4 border-top" style={{ background: '#f8f9fb' }}>
                  <div className="fw-semibold small mb-2"><i className="fas fa-reply me-1 text-primary"></i>Reply to {selected.name}</div>
                  {replyOk && <div className="alert alert-success py-2 mb-2 small"><i className="fas fa-check-circle me-1"></i>Reply sent successfully.</div>}
                  {replyErr && <div className="alert alert-danger py-2 mb-2 small">{replyErr}</div>}
                  <textarea
                    className="form-control mb-2"
                    rows={4}
                    placeholder={`Type your reply to ${selected.email}…`}
                    value={replyText}
                    onChange={e => { setReplyText(e.target.value); setReplyOk(false); setReplyErr(''); }}
                    style={{ resize: 'vertical', fontSize: '0.88rem' }}
                  />
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted small">Sends to: <strong>{selected.email}</strong></span>
                    <button className="btn btn-primary rounded-pill px-4" onClick={sendReply} disabled={replying || !replyText.trim()}>
                      {replying ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending…</> : <><i className="fas fa-paper-plane me-2"></i>Send Reply</>}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
